import * as Constants from 'Constants/Constants';

/**
 * Validates if minimum cutoff rules are complete for the given rule type
 * @param orderRange - The order range type (DAILY, WEEKLY, MONTHLY)
 * @param cutoffDay - The cutoff day (required for WEEKLY rules, optional for DAILY)
 * @param orderDueBy - The order due by rule (optional for DAILY rules)
 * @returns true if the rules are complete and valid
 */
export const isMinimumCutoffRuleComplete = (
  orderRange: string | undefined,
  cutoffDay: string | undefined,
  orderDueBy?: string | undefined,
): boolean => {
  if (!orderRange) return false;

  switch (orderRange) {
    case Constants.ORDER_RANGE_OPTIONS.DAILY:
      // For DAILY rules, either cutoffDay OR orderDueBy should be present,
      // or both can be empty strings (which defaults to same-day ordering)
      return !!(cutoffDay || orderDueBy) || (cutoffDay === '' && orderDueBy === '');
    case Constants.ORDER_RANGE_OPTIONS.WEEKLY:
      // WEEKLY rules require cutoffDay to be a valid day of week (0-6)
      const dayOfWeek = parseInt(cutoffDay || '');
      return !isNaN(dayOfWeek) && dayOfWeek >= 0 && dayOfWeek <= 6;
    case Constants.ORDER_RANGE_OPTIONS.MONTHLY:
      return true; // Monthly only requires orderRange
    default:
      return false;
  }
};

/**
 * Validates if maximum cutoff rules are complete for the given rule type
 * @param maxOrderRange - The maximum order range type (DAYS, WEEKS, MONTHS)
 * @param maxCutoffDays - Days value (required for DAYS range)
 * @param maxCutoffWeeks - Weeks value (required for WEEKS range)
 * @param maxCutoffMonths - Months value (required for MONTHS range)
 * @returns true if the rules are complete and valid
 */
export const isMaximumCutoffRuleComplete = (
  maxOrderRange: string | undefined,
  maxCutoffDays: string | undefined,
  maxCutoffWeeks: string | undefined,
  maxCutoffMonths: string | undefined,
): boolean => {
  if (!maxOrderRange) return false;

  switch (maxOrderRange) {
    case Constants.MAX_ORDER_RANGE_OPTIONS.DAYS:
      return !!maxCutoffDays;
    case Constants.MAX_ORDER_RANGE_OPTIONS.WEEKS:
      return !!maxCutoffWeeks;
    case Constants.MAX_ORDER_RANGE_OPTIONS.MONTHS:
      return !!maxCutoffMonths;
    default:
      return false;
  }
};
